import { useRef, useCallback, useEffect, useState } from 'react';

/**
 * CurrencyInput — Brazilian progressive-cents masking
 *
 * Mask pattern (centavos progressivos):
 *   type "1"       → 0,01
 *   type "10"      → 0,10
 *   type "100"     → 1,00
 *   type "1000"    → 10,00
 *   type "10000"   → 100,00
 *   type "100000"  → 1.000,00
 *
 * - Formats IN REAL TIME while typing (no blur needed)
 * - Always emits a clean JS `number` via `onChange`
 * - inputMode="decimal" triggers numeric keyboard on mobile
 * - Supports editing existing values, paste, and backspace
 * - Works with any `maxDecimals` (default 2, crypto can use 8)
 */

interface CurrencyInputProps {
  /** Current numeric value */
  value: number;
  /** Called with the parsed numeric value on every keystroke */
  onChange: (value: number) => void;
  /** Placeholder shown when empty */
  placeholder?: string;
  /** TailwindCSS class string applied to the <input> */
  className?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Whether the field is required */
  required?: boolean;
  /** Whether the field is read-only */
  readOnly?: boolean;
  /** Max decimal places (default: 2). Use 8 for crypto. */
  maxDecimals?: number;
  /** If true, allow negative values */
  allowNegative?: boolean;
}

/**
 * Convert a raw digit string (e.g. "12345") to pt-BR display string.
 * maxDecimals=2: "12345" → "123,45"   "100000" → "1.000,00"
 */
const digitsToDisplay = (digits: string, decimals: number): string => {
  if (!digits) return '';
  const padded = digits.padStart(decimals + 1, '0');
  const intStr = padded.slice(0, padded.length - decimals) || '0';
  const decStr = decimals > 0 ? padded.slice(padded.length - decimals) : '';
  const intFormatted = parseInt(intStr, 10).toLocaleString('pt-BR');
  return decimals > 0 ? `${intFormatted},${decStr}` : intFormatted;
};

/**
 * Convert a numeric value to the raw digit string used internally.
 * value=123.45, decimals=2 → "12345"
 * value=0        → ""
 */
const valueToDigits = (value: number, decimals: number): string => {
  if (!value || value === 0) return '';
  const abs = Math.abs(value);
  // toFixed gives us exact decimals, remove the dot, strip leading zeros
  const str = abs.toFixed(decimals).replace('.', '');
  return str.replace(/^0+/, '') || '';
};

export const CurrencyInput = ({
  value,
  onChange,
  placeholder = '0,00',
  className = '',
  disabled,
  required,
  readOnly,
  maxDecimals = 2,
  allowNegative = false,
}: CurrencyInputProps) => {
  const [digits, setDigits] = useState<string>(() => valueToDigits(value, maxDecimals));
  const [isNeg, setIsNeg] = useState<boolean>(allowNegative && value < 0);

  // Track the last value we set so we don't re-sync when our own onChange fires
  const prevValueRef = useRef<number>(value);

  // Sync state when the external `value` prop changes (e.g. form reset, loading
  // an existing record, or parent clearing the field)
  useEffect(() => {
    const diff = Math.abs((value ?? 0) - prevValueRef.current);
    if (diff > 0.000001) {
      setDigits(valueToDigits(value, maxDecimals));
      setIsNeg(allowNegative && value < 0);
      prevValueRef.current = value;
    }
  }, [value, maxDecimals, allowNegative]);

  const displayValue = digits
    ? (isNeg ? '-' : '') + digitsToDisplay(digits, maxDecimals)
    : '';

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (readOnly || disabled) return;

      const raw = e.target.value;

      // Detect negative sign at the start
      const hasNeg = allowNegative && raw.startsWith('-');

      // Keep only digit characters
      const extracted = raw.replace(/\D/g, '');

      // Strip leading zeros — they get added back by progressive formatting
      const cleaned = extracted.replace(/^0+/, '') || '';

      // Hard cap: 13 digits → max 99,999,999,999.99 (enough for any real use case)
      const limited = cleaned.slice(0, 13);

      setDigits(limited);
      if (allowNegative) setIsNeg(hasNeg);

      // Emit numeric value
      const numVal = limited
        ? parseInt(limited, 10) / Math.pow(10, maxDecimals)
        : 0;
      const finalVal = allowNegative && hasNeg ? -numVal : numVal;

      prevValueRef.current = finalVal;
      onChange(finalVal);
    },
    [maxDecimals, allowNegative, onChange, readOnly, disabled],
  );

  return (
    <input
      type="text"
      inputMode="decimal"
      value={displayValue}
      onChange={handleChange}
      placeholder={placeholder}
      className={className}
      disabled={disabled}
      required={required}
      readOnly={readOnly}
    />
  );
};
